import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ChevronLeft, ShieldCheck, Mail, Phone } from "lucide-react";
import { AppHeader, SourceNote } from "@/components/Layout";
import { PlanPicker } from "@/components/PlanPicker";
import { api, errMsg, tokens, STATES, ALLERGENS, ALERT_TYPES } from "@/lib/api";
import { LEGAL } from "@/components/LegalLayout";
import { useSeo } from "@/lib/seo";

const Choice = ({ selected, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`flex w-full items-center justify-between rounded-md border px-4 py-3 text-left text-sm font-medium transition-colors ${
      selected ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
    }`}
  >
    {children}
    {selected && <Check className="h-4 w-4" />}
  </button>
);

const ChannelPick = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="How should we send your code?">
    <button
      type="button"
      data-testid="channel-email"
      onClick={() => onChange("email")}
      className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-semibold transition-colors ${
        value === "email" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      <Mail className="h-4 w-4" /> Email <span className="text-[10px] font-normal opacity-70">(default)</span>
    </button>
    <button
      type="button"
      data-testid="channel-sms"
      onClick={() => onChange("sms")}
      className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-semibold transition-colors ${
        value === "sms" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      <Phone className="h-4 w-4" /> SMS
    </button>
  </div>
);

export default function Onboarding() {
  useSeo({
    title: "Sign up for Australian food recall SMS alerts",
    description:
      "Set up Australian food recall alerts in about a minute: verify your mobile, pick your state and your allergens, and get one SMS per relevant FSANZ recall.",
    path: "/join",
  });
  const [params] = useSearchParams();
  useEffect(() => {
    const ref = params.get("ref");
    if (ref) localStorage.setItem("fra_ref", ref.toUpperCase());
  }, [params]);
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [channel, setChannel] = useState("email");
  const [sentChannel, setSentChannel] = useState("email");
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(true);
  const [prefs, setPrefs] = useState({ state: "", coverage: "all", alert_types: ["all"], allergies: [] });

  const sendCode = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/request-code", { mobile, email, channel, mode: "signup" });
      setDevCode(r.dev_code || null);
      setSentChannel(r.channel || channel);
      setSentTo(r.destination || mobile);
      setStep(3);
      const where = (r.channel || channel) === "email" ? "email" : "phone";
      toast.success(r.mocked ? "Verification code generated (SMS mocked)" : `Verification code sent to your ${where}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/verify", { mobile, code });
      tokens.set(r.token);
      setStep(4);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const save = async (extra = {}, next) => {
    setBusy(true);
    try {
      await api.put("/me", { ...prefs, ...extra });
      setStep(next);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.put("/me", {
        ...prefs, onboarded: true, consent_version: LEGAL.version,
        ref: localStorage.getItem("fra_ref") || null,
      });
      localStorage.removeItem("fra_ref");
      setStep(8);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const toggleType = (key) => {
    setPrefs((p) => {
      if (key === "all") return { ...p, alert_types: ["all"] };
      const base = p.alert_types.filter((t) => t !== "all");
      const next = base.includes(key) ? base.filter((t) => t !== key) : [...base, key];
      return { ...p, alert_types: next.length ? next : ["all"] };
    });
  };

  const toggleAllergy = (a) => setPrefs((p) => ({
    ...p, allergies: p.allergies.includes(a) ? p.allergies.filter((x) => x !== a) : [...p.allergies, a],
  }));

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSendCode = mobile.length >= 8 && emailOk && consent && !busy;

  const sentWhere = sentChannel === "email" ? (sentTo || email) : mobile;

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <div className="mx-auto max-w-xl px-5 py-10">
        <div className="mb-6 flex items-center justify-between">
          <button
            data-testid="onboarding-back"
            onClick={() => (step > 1 ? setStep(step - 1) : nav("/"))}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <span className="label-caps" data-testid="onboarding-step">Step {step} of 8</span>
        </div>
        <div className="mb-8 h-1 w-full rounded-full bg-slate-200">
          <div className="h-1 rounded-full bg-red-700 transition-all" style={{ width: `${(step / 8) * 100}%` }} />
        </div>

        <motion.div key={step} initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}
          className="rounded-md border border-slate-200 bg-white p-6 sm:p-8" data-testid="onboarding-card">
          {step === 1 && (
            <div className="space-y-5">
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">Never miss an Australian food recall.</h1>
              <p className="text-slate-600">Get important food recalls sent directly to your phone.</p>
              <button data-testid="start-onboarding" onClick={() => setStep(2)}
                className="w-full rounded-md bg-red-700 px-5 py-3 font-semibold text-white hover:bg-red-800">
                Get recall alerts
              </button>
              <SourceNote />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-2xl font-semibold text-slate-900">Your details</h2>
              <div>
                <label className="label-caps" htmlFor="mobile">Australian mobile</label>
                <input id="mobile" data-testid="mobile-input" value={mobile} onChange={(e) => setMobile(e.target.value)}
                  placeholder="04XX XXX XXX" inputMode="tel"
                  className="mt-2 w-full rounded-md border border-slate-300 px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
                <p className="mt-2 text-xs text-slate-500">We text recall alerts to this number.</p>
              </div>
              <div>
                <label className="label-caps" htmlFor="email">Email address</label>
                <input id="email" data-testid="email-input" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" inputMode="email" type="email" autoComplete="email"
                  className="mt-2 w-full rounded-md border border-slate-300 px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
                <p className="mt-2 text-xs text-slate-500">We send a welcome email and keep this on your account.</p>
              </div>
              <div>
                <p className="label-caps mb-2">Send the verification code via</p>
                <ChannelPick value={channel} onChange={setChannel} />
              </div>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-4">
                <input
                  type="checkbox"
                  data-testid="consent-checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-red-700"
                />
                <span className="text-xs leading-relaxed text-slate-600">
                  I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noreferrer" data-testid="consent-terms-link"
                    className="font-medium text-slate-900 underline">Terms of Service</a>{" "}
                  and{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer" data-testid="consent-privacy-link"
                    className="font-medium text-slate-900 underline">Privacy Policy</a>, and consent to
                  receiving SMS alerts. Reply STOP any time to unsubscribe.
                </span>
              </label>
              <button data-testid="send-code-btn" disabled={!canSendCode} onClick={sendCode}
                className="w-full rounded-md bg-slate-900 px-5 py-3 font-semibold text-white disabled:opacity-40">
                {busy ? "Sending..." : "Send verification code"}
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-2xl font-semibold text-slate-900">Enter your code</h2>
              <p className="text-sm text-slate-600" data-testid="code-sent-note">
                We sent a code via {sentChannel === "email" ? "email" : "SMS"} to{" "}
                <span className="font-medium text-slate-900">{sentWhere}</span>.
              </p>
              {devCode && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="dev-code-notice">
                  SMS is mocked. Your code is <b className="font-mono">{devCode}</b>.
                </div>
              )}
              <input data-testid="code-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric"
                placeholder="123456" maxLength={6}
                className="w-full rounded-md border border-slate-300 px-4 py-3 text-center text-2xl tracking-[0.4em] font-mono focus:outline-none focus:ring-2 focus:ring-slate-900" />
              <button data-testid="verify-code-btn" disabled={busy || code.length < 6} onClick={verify}
                className="w-full rounded-md bg-slate-900 px-5 py-3 font-semibold text-white disabled:opacity-40">
                {busy ? "Verifying..." : "Verify number"}
              </button>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <h2 className="text-2xl font-semibold text-slate-900">Where do you live?</h2>
              <div className="grid grid-cols-2 gap-3">
                {STATES.map((s) => (
                  <Choice key={s} testid={`state-${s}`} selected={prefs.state === s} onClick={() => setPrefs({ ...prefs, state: s })}>{s}</Choice>
                ))}
              </div>
              <button data-testid="state-next" disabled={!prefs.state || busy} onClick={() => save({}, 5)}
                className="w-full rounded-md bg-slate-900 px-5 py-3 font-semibold text-white disabled:opacity-40">Continue</button>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <h2 className="text-2xl font-semibold text-slate-900">Which recalls do you want?</h2>
              <div className="space-y-3">
                <Choice testid="coverage-all" selected={prefs.coverage === "all"} onClick={() => setPrefs({ ...prefs, coverage: "all" })}>
                  All Australian recalls (recommended)
                </Choice>
                <Choice testid="coverage-state" selected={prefs.coverage === "state_national"} onClick={() => setPrefs({ ...prefs, coverage: "state_national" })}>
                  My state + national recalls only
                </Choice>
              </div>
              <button data-testid="coverage-next" disabled={busy} onClick={() => save({}, 6)}
                className="w-full rounded-md bg-slate-900 px-5 py-3 font-semibold text-white disabled:opacity-40">Continue</button>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-5">
              <h2 className="text-2xl font-semibold text-slate-900">What alerts would you like?</h2>
              <div className="space-y-3">
                {ALERT_TYPES.map((t) => (
                  <Choice key={t.key} testid={`alert-type-${t.key}`} selected={prefs.alert_types.includes(t.key)} onClick={() => toggleType(t.key)}>
                    {t.label}
                  </Choice>
                ))}
              </div>
              <button data-testid="alert-types-next" disabled={busy} onClick={() => save({}, 7)}
                className="w-full rounded-md bg-slate-900 px-5 py-3 font-semibold text-white disabled:opacity-40">Continue</button>
            </div>
          )}

          {step === 7 && (
            <div className="space-y-5">
              <h2 className="text-2xl font-semibold text-slate-900">Any allergies we should prioritise?</h2>
              <p className="text-sm text-slate-600">We'll make these alerts stand out. This does not hide other recalls.</p>
              <div className="grid grid-cols-2 gap-3">
                {ALLERGENS.map((a) => (
                  <Choice key={a} testid={`allergy-${a.toLowerCase().replace(/[^a-z]/g, "-")}`} selected={prefs.allergies.includes(a)} onClick={() => toggleAllergy(a)}>{a}</Choice>
                ))}
              </div>
              <Choice testid="allergy-none" selected={prefs.allergies.length === 0} onClick={() => setPrefs({ ...prefs, allergies: [] })}>None</Choice>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                <p className="label-caps">You're protected</p>
                <dl className="mt-3 space-y-1 text-sm text-slate-700" data-testid="confirmation-summary">
                  <div><dt className="inline font-medium">State: </dt><dd className="inline">{prefs.state || "-"}</dd></div>
                  <div><dt className="inline font-medium">Coverage: </dt><dd className="inline">{prefs.coverage === "all" ? "All Australia" : "My state + National"}</dd></div>
                  <div><dt className="inline font-medium">Alerts: </dt><dd className="inline">{prefs.alert_types.map((k) => ALERT_TYPES.find((t) => t.key === k)?.label).join(", ")}</dd></div>
                  <div><dt className="inline font-medium">Priority allergies: </dt><dd className="inline">{prefs.allergies.join(", ") || "None"}</dd></div>
                </dl>
              </div>
              <button data-testid="finish-onboarding" disabled={busy} onClick={finish}
                className="w-full rounded-md bg-red-700 px-5 py-3 font-semibold text-white hover:bg-red-800 disabled:opacity-40">
                {busy ? "Saving..." : "Save and turn on alerts"}
              </button>
              <p className="flex items-start gap-2 text-xs text-slate-500">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                We use your allergy selections only to prioritise relevant food recall alerts.
              </p>
            </div>
          )}

          {step === 8 && (
            <div className="space-y-5">
              <h2 className="text-2xl font-semibold text-slate-900">Turn on your SMS alerts</h2>
              <p className="text-sm text-slate-600">
                Your preferences are saved and a welcome email is on its way. Browsing recalls is always free.
                A subscription covers the cost of texting you the moment a relevant recall is published.
              </p>
              <PlanPicker />
              <button
                data-testid="skip-subscription"
                onClick={() => nav("/alerts")}
                className="w-full rounded-md border border-slate-300 px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Not now - just browse recalls
              </button>
              <SourceNote />
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
